import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

interface HelpSection {
  id: string;
  title: string;
  paragraphs: string[];
}

// The time-related sections are the ones users most often conflate, so they get
// an always-visible comparison table (below) rather than being buried in the
// collapsible per-section accordions.
const TIME_FAMILY: { name: string; purpose: string; example: string }[] = [
  {
    name: 'Work Timers',
    purpose: 'Live work sessions you start and stop right now (Clockify-style).',
    example: 'Start a timer when you begin focused work; stop it when you finish.',
  },
  {
    name: 'Time Entries',
    purpose:
      'The actual time you have logged — the record of what you did, for reporting and manual backfill.',
    example: "Review this week's tracked hours, or add an entry you forgot to time live.",
  },
  {
    name: 'Planned Schedule',
    purpose:
      'Planned / calendar time: blocks imported from calendar files or Google Calendar. What you intend to do — not tracked time.',
    example: 'See the meetings and focus blocks already on your calendar.',
  },
  {
    name: 'Projects',
    purpose: 'Projects and their hierarchical tasks. Work is organised and rolls up here.',
    example: 'Break a project into tasks and sub-tasks and track their status.',
  },
  {
    name: 'Timelines',
    purpose: 'Multi-year goals and milestones for long-horizon planning.',
    example: 'Map a 10-year goal with checkpoint milestones along the way.',
  },
  {
    name: 'Countdowns',
    purpose:
      'Count-ups and countdowns to meaningful life-event dates, plus recurring reminders. (Formerly labelled "Life Events".)',
    example: 'Days until a birthday; days since you started a streak.',
  },
];

const SECTION_GROUPS: { group: string; sections: HelpSection[] }[] = [
  {
    group: 'Daily tracking',
    sections: [
      {
        id: 'dashboard',
        title: 'Dashboard',
        paragraphs: [
          'Your at-a-glance home screen. It surfaces what needs attention today — habits that are due, quick-log controls, and a Countdowns widget — so you can act without hunting through the app.',
        ],
      },
      {
        id: 'habits',
        title: 'Habits',
        paragraphs: [
          'Create and track recurring routines with daily, weekly, monthly, yearly, or custom recurrence. Switch between a card grid and a compact list view; in list view you can choose which columns to show.',
          'Each habit has quick actions: Mark Done (or +1 toward a multi-count target), Snooze, History, Stats, and Export. Tick the checkboxes to select several habits at once, then use the toolbar that appears: "Edit selected" opens the bulk-edit dialog, and "Export" downloads a combined report.',
          'Bulk edit is opt-in per field — only the fields whose checkbox you tick are changed (active status, check-in enabled, check-in priority, recurrence type, recurrence interval, target count). Every other setting on the selected habits is left untouched. After applying, a summary tells you how many succeeded and lists any that failed, with the reason.',
        ],
      },
      {
        id: 'check-in',
        title: 'Check-In',
        paragraphs: [
          'A guided, time-aware review queue — distinct from ordinary habit logging (see "Check-In vs. logging a habit" above). It walks you through the habits you have opted into, during their configured time windows, and records a structured response for each.',
          'Enable it per habit with the "Include in the check-in queue" toggle, and optionally give a habit a higher priority so it surfaces first. Check-in statistics are tracked separately from the plain Done/+N logs.',
        ],
      },
    ],
  },
  {
    group: 'Time & planning',
    sections: [
      {
        id: 'work-timers',
        title: 'Work Timers',
        paragraphs: [
          'A live session tracker: start a timer when you begin working and stop it when you are done. It is intentionally separate from Countdowns (life-event anchors) and from Time Entries (the stored records).',
        ],
      },
      {
        id: 'time-entries',
        title: 'Time Entries',
        paragraphs: [
          'The system of record for time you have actually spent. Finished work timers land here, and you can also add or edit entries by hand to backfill time you forgot to track live. This is where reporting on real, tracked time happens.',
        ],
      },
      {
        id: 'planned-schedule',
        title: 'Planned Schedule',
        paragraphs: [
          'Planned calendar blocks imported from ICS files or a Google Calendar sync. These represent time you intend to spend — they are deliberately kept separate from Time Entries, which record time you actually spent.',
        ],
      },
      {
        id: 'projects',
        title: 'Projects',
        paragraphs: [
          'Organise work into projects and hierarchical tasks (tasks and sub-tasks). Projects give your time tracking and planning a structure to roll up into.',
        ],
      },
      {
        id: 'timelines',
        title: 'Timelines',
        paragraphs: [
          'Long-horizon planning: multi-year goals with start and end dates and milestone checkpoints. Use this for the decade-scale ambitions that outlast any single habit.',
        ],
      },
      {
        id: 'countdowns',
        title: 'Countdowns',
        paragraphs: [
          'Live-updating anchors to meaningful dates — count up from a date that has passed, count down to one ahead, or set recurring reminders. This section was previously labelled "Life Events"; the page and its data are unchanged, only the name.',
        ],
      },
    ],
  },
  {
    group: 'Journaling, data & integrations',
    sections: [
      {
        id: 'reflections',
        title: 'Reflections',
        paragraphs: [
          'An encrypted personal journal. Entries are always encrypted at rest so your private writing stays private, and you can optionally add a passcode lock that hides their contents until you enter it.',
        ],
      },
      {
        id: 'exports',
        title: 'Exports',
        paragraphs: [
          'Back up and restore your data, and generate an AI-context export you can paste into an assistant. Habit reports can also be exported directly from the Habits page.',
        ],
      },
      {
        id: 'integrations',
        title: 'Integrations',
        paragraphs: [
          'Connect external services — for example, importing calendar events that appear on your Planned Schedule.',
        ],
      },
      {
        id: 'activity',
        title: 'Activity',
        paragraphs: [
          'A log of bulk and administrative operations. Each entry shows what changed and how many items succeeded or failed; open one to see the per-item outcome and the exact reason anything failed. This is where a bulk habit edit is recorded so you can review it later.',
        ],
      },
    ],
  },
  {
    group: 'Personalizing the app',
    sections: [
      {
        id: 'navigation',
        title: 'Customizing navigation & display',
        paragraphs: [
          'Open the account menu (top-right) and choose "Nav settings…" to reorder the navigation bar, hide sections you do not use, or reset to the default order. Your layout is saved in this browser, so it does not sync across devices.',
          'The same account menu toggles a compact layout, and the Profile page holds dark mode, display density, and an analytics reference date for reviewing historical data.',
        ],
      },
    ],
  },
];

const Help: React.FC = () => {
  const { cardContentPaddingSx, pageHeaderSx, sectionGapSx, tableCellPaddingSx } =
    useLayoutTokens();

  return (
    <PageShell maxWidth="standard">
      <Box sx={pageHeaderSx}>
        <Typography variant="h3" component="h1">
          Help &amp; Guide
        </Typography>
        <Typography variant="body1" color="text.secondary">
          What each section does, and how the pieces fit together.
        </Typography>
      </Box>

      <Card sx={sectionGapSx}>
        <CardContent sx={cardContentPaddingSx}>
          <Typography variant="h6" gutterBottom>
            Understanding the time-related sections
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Several sections deal with time in different ways. Here is the quick distinction:
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>What it is for</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Example</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {TIME_FAMILY.map((row) => (
                  <TableRow key={row.name}>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {row.name}
                    </TableCell>
                    <TableCell>{row.purpose}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.example}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card sx={sectionGapSx}>
        <CardContent sx={cardContentPaddingSx}>
          <Typography variant="h6" gutterBottom>
            Check-In vs. logging a habit
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Chip label="Logging a habit" color="primary" size="small" sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                The quick Mark Done / +1 controls on the Habits page and Dashboard. A one-tap
                record that you did the habit — use it any time, for any habit.
              </Typography>
            </Box>
            <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Box sx={{ flex: 1 }}>
              <Chip label="Check-In" color="secondary" size="small" sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                A separate, guided queue (the Check-In page) that surfaces opted-in habits during
                their time windows and asks for a structured response. It is time-aware and its
                stats are tracked separately from ordinary logging.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {SECTION_GROUPS.map((group) => (
        <Box key={group.group} sx={sectionGapSx}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            {group.group}
          </Typography>
          {group.sections.map((section) => (
            <Accordion key={section.id} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${section.id}-content`}>
                <Typography sx={{ fontWeight: 600 }}>{section.title}</Typography>
              </AccordionSummary>
              <AccordionDetails id={`${section.id}-content`}>
                {section.paragraphs.map((paragraph, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: index < section.paragraphs.length - 1 ? 1.5 : 0 }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}
    </PageShell>
  );
};

export default Help;
